import { Injectable } from '@angular/core';
import { TabItem } from '@app-types/common';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { PermissionType } from '@app-types/enums/permission-type';
import { PermissionService } from '@app-services/permission/permission.service';

const {
  ORGANIZATION,
  ORGANIZATIONS,
  PROVIDERS,
  MAIL_ACCOUNTS,
  MAIL_ACCOUNT,
  GENERAL,
  USERS,
  RULES,
  FOLDERS,
  TEMPLATES,
  TAGS,
  GROUPS,
  ADDRESS_BOOK,
} = ROUTES;

@Injectable({ providedIn: 'root' })
export class PermissionNavTabHelper {
  allTabs = [
    {
      label: 'generalNavTab',
      route: `/${ORGANIZATION}/${GENERAL}`,
      permission: PermissionType.CanManageOrganization,
    },
    {
      label: 'mailAccountsNavTab',
      route: `/${ORGANIZATION}/${MAIL_ACCOUNTS}`,
      permission: PermissionType.CanSearchMailAccounts,
    },
    {
      label: 'usersNavTab',
      route: `/${ORGANIZATION}/${USERS}`,
      permission: PermissionType.CanManageUsers,
    },
    {
      label: 'groupsNavTab',
      route: `/${ORGANIZATION}/${GROUPS}`,
      permission: PermissionType.CanManageGroups,
    },
  ];

  allTabsForMailAccount = [
    {
      label: 'generalNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${GENERAL}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'usersNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${USERS}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'dispatchingRulesNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${RULES}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'tagsNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${TAGS}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'templates',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${TEMPLATES}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'foldersNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${FOLDERS}`,
      permission: PermissionType.CanManageMailAccount,
    },
    {
      label: 'addressBookNavTab',
      route: (mailAccountId: number) =>
        `/${ORGANIZATION}/${MAIL_ACCOUNT}/${mailAccountId}/${ADDRESS_BOOK}`,
      permission: PermissionType.CanSearchMailAccountContacts,
    },
  ];

  constructor(private permissionService: PermissionService) {}

  public getSettingPageTabs(): TabItem[] {
    return [
      {
        index: 0,
        label: 'companiesNavTab',
        route: `/${ORGANIZATIONS}`,
      },
      {
        index: 1,
        label: 'providersNavTab',
        route: `/${PROVIDERS}`,
      },
    ];
  }

  public async getOrganizationPageTabs(
    organisationId: number
  ): Promise<TabItem[]> {
    const tabsWithPermission = await Promise.all(
      this.allTabs.map(async ({ permission, ...tab }) => ({
        tab: { ...tab },
        allowed: await this.permissionService.hasPermissionOver(
          organisationId,
          null,
          permission
        ),
      }))
    );
    return tabsWithPermission
      .filter(({ allowed }) => allowed)
      .map(({ tab }, index) => ({ ...tab, index }));
  }

  public async getTabsForMailAccountPage(
    mailAccountId: number,
    organisationId?: number
  ): Promise<TabItem[]> {
    const canManageMailAccounts = organisationId
      ? await this.permissionService.hasPermissionOver(
          organisationId,
          null,
          PermissionType.CanManageMailAccounts
        )
      : false;

    const tabsWithPermission = await Promise.all(
      this.allTabsForMailAccount.map(async ({ permission, ...tab }) => ({
        tab: { ...tab, route: tab?.route?.(mailAccountId) },
        allowed: canManageMailAccounts
          ? true
          : await this.permissionService.hasPermissionOver(
              null,
              mailAccountId,
              permission
            ),
      }))
    );
    return tabsWithPermission
      .filter(({ allowed }) => allowed)
      .map(({ tab }, index) => ({ ...tab, index }));
  }

  async getMailAccountPagesLink(
    mailAccountId: number,
    organisationId?: number
  ): Promise<string | undefined> {
    const tabs = await this.getTabsForMailAccountPage(
      mailAccountId,
      organisationId
    );
    return tabs.length ? tabs[0].route : undefined;
  }
}
